<template>
  <!--
    TODO:
    - Handle swipe ?
  -->
  <div
    :id="`db-carousel-${uid}`"
    class="db-carousel"
  >
    <!-- TODO: xo7: Check buttons texts -->
    <div
      v-if="showVideoButton || showFullscreenButton"
      class="db-carousel-lightbox-controls"
    >
      <button
        v-if="showVideoButton"
        type="button"
        class="db-button-supporting db-button-circle db-carousel-button-fullscreen"
        v-on:click="$emit('click-video')"
      >
        <span class="db-visually-hidden">
          {{ $t('carousel.Open video in fullscreen') }}
        </span>
        <IconDbVideo
          class="db-carousel-button-video-icon"
          width="19"
          height="19"
        />
      </button>
      <button
        v-if="showFullscreenButton"
        type="button"
        class="db-button-supporting db-button-circle db-carousel-button-video"
        v-on:click="$emit('click-fullscreen', currentSlideIndex)"
      >
        <span class="db-visually-hidden">
          {{ $t('carousel.Fullscreen') }}
        </span>
        <IconDbFullscreen
          class="db-carousel-button-fullscreen-icon"
          width="18"
          height="18"
        />
      </button>
    </div>

    <!-- Controls -->
    <div
      v-if="length > 1"
      class="db-carousel-arrow-controls"
    >
      <button
        type="button"
        class="db-button-square db-button-supporting db-button-small db-carousel-control db-carousel-button-prev"
        v-on:click="goToPreviousSlide()"
      >
        <span class="db-visually-hidden">
          {{ $t('carousel.Previous slide') }}
        </span>
        <IconDbChevron
          width="10"
          height="10"
        />
      </button>
      <button
        type="button"
        class="db-button-square db-button-supporting db-button-small db-carousel-control db-carousel-button-next"
        v-on:click="goToNextSlide()"
      >
        <span class="db-visually-hidden">
          {{ $t('carousel.Next slide') }}
        </span>
        <IconDbChevron
          class="db-rotate-180"
          width="10"
          height="10"
        />
      </button>
    </div>

    <div
      v-if="$slots.thumbnail && length > 1"
      class="db-carousel-control db-hide-scrollbar"
      role="tablist"
    >
      <button
        v-for="(_, index) in Array(length)"
        :id="`control-tab-${uid}-${index}`"
        :key="`control-tab-${uid}-${index}`"
        class="db-content-cover"
        type="button"
        role="tab"
        :aria-controls="`slide-${uid}-${index}`"
        :aria-selected="index === currentSlideIndex"
        :tabindex="currentSlideIndex === index ? 0 : -1"
        v-on:click="setCurrentSlideIndex(index)"
        v-on:focus="setCurrentSlideIndex(index)"
        v-on:keydown.left.prevent="goToPreviousSlide('focus')"
        v-on:keydown.right.prevent="goToNextSlide('focus')"
        v-on:keydown.home.prevent="focusTabIndex(0)"
        v-on:keydown.end.prevent="focusTabIndex(length - 1)"
      >
        <slot
          name="thumbnail"
          :index="index"
        />
      </button>
    </div>
    <!-- !Controls -->

    <!-- Slides -->
    <div class="db-carousel-content">
      <TransitionGroup :name="currentCarouselTransition">
        <div
          v-for="(_, index) in Array(length)"
          v-show="index === currentSlideIndex"
          :id="`slide-${uid}-${index}`"
          :key="`slide-${uid}-${index}`"
          :class="[
            'db-carousel-slide db-content-cover',
            { 'db-current-slide': index === currentSlideIndex },
          ]"
          :tabindex="$slots.thumbnail && index === currentSlideIndex ? 0 : -1"
          :role="$slots.thumbnail ? 'tabpanel' : undefined"
        >
          <slot
            name="default"
            :index="index"
            :currentSlideIndex="currentSlideIndex"
          >
            {{ $t('slide number {x}', { x: index }) }}
          </slot>
        </div>
      </TransitionGroup>

      <button
        v-if="length > 1"
        type="button"
        class="db-button-square db-button-supporting db-button-small db-carousel-control db-carousel-mobile db-carousel-button-prev"
        v-on:click="goToPreviousSlide()"
      >
        <span class="db-visually-hidden">
          {{ $t('carousel.Previous slide') }}
        </span>
        <IconDbChevron
          width="10"
          height="10"
        />
      </button>

      <button
        v-if="length > 1"
        type="button"
        class="db-button-square db-button-supporting db-button-small db-carousel-control db-carousel-mobile db-carousel-button-next"
        v-on:click="goToNextSlide()"
      >
        <span class="db-visually-hidden">
          {{ $t('carousel.Next slide') }}
        </span>
        <IconDbChevron
          class="db-rotate-180"
          width="10"
          height="10"
        />
      </button>
    </div>
    <!-- !Slides -->
  </div>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { ref, onBeforeMount, useSlots, watch } from 'vue'
import {
  useIsScrollableInline,
  useMaintainScrollVisibilityInline,
} from '@/composables/useElementOverflow'

const props = defineProps<{
  /** Amount of slides */
  length: number
  /** Index of the slide active by default */
  defaultSlideIndex?: number
  /** Displays video button */
  showVideoButton?: boolean
  /** Displays video button */
  showFullscreenButton?: boolean
}>()

defineEmits<{
  (e: 'click-video'): void
  (e: 'click-fullscreen', index: number): void
}>()

/** Animation between slides */
const currentCarouselTransition = ref<'db-slidein-left' | 'db-slidein-right'>('db-slidein-left')

/** Index of the slide currently displayed */
const currentSlideIndex = ref(0)

onBeforeMount(() => {
  /** If defaultSlideIndex prop exist, init default slide display */
  const slideIndex = props.defaultSlideIndex

  if (slideIndex) {
    setCurrentSlideIndex(slideIndex)
  }
})

/** Unique id for binding */
const uid = uuidv4()

/** Get the slots to check which is on */
const slots = useSlots()

/** Set the index of the slide currently displayed  */
function setCurrentSlideIndex(index: number) {
  if (index !== currentSlideIndex.value) {
    if (slots.thumbnail) {
      scrollThumbnails(index)
    }

    /** Set the new slide value */
    currentSlideIndex.value = index
  }
}

/** Find previous slide index and call setCurrentSlideIndex or focusTabIndex */
function goToPreviousSlide(action?: 'focus' | null) {
  const prevTabIndex =
    currentSlideIndex.value === 0 ? props.length - 1 : (currentSlideIndex.value as number) - 1

  switch (action) {
    case 'focus':
      focusTabIndex(prevTabIndex)
      break
    default:
      setCurrentSlideIndex(prevTabIndex)
      break
  }
}

/** Find next tab index and call setCurrentSlideIndex or focusTabIndex */
function goToNextSlide(action?: 'focus' | null) {
  const nextTabIndex =
    currentSlideIndex.value === props.length - 1 ? 0 : (currentSlideIndex.value as number) + 1

  switch (action) {
    case 'focus':
      focusTabIndex(nextTabIndex)
      break
    default:
      setCurrentSlideIndex(nextTabIndex)
      break
  }
}

/**
 * Pause the video when current changing slide
 */
function pauseVideo() {
  const currentSlide = document.querySelector('.db-current-slide')
  if (currentSlide?.parentElement?.querySelectorAll('iframe').length) {
    // eslint-disable-next-line no-self-assign
    currentSlide?.parentElement?.querySelectorAll('iframe').forEach((v) => {
      const old_src = v.src
      v.src = old_src
    })
  }
}

/** Set focus to tab button at `index` */
function focusTabIndex(index: number) {
  const tabElement = document.querySelector<HTMLElement>(`#control-tab-${uid}-${index}`)

  if (tabElement) {
    tabElement.focus()
  }
}

/** Handle auto scroll in tablist to always display selected tab */
function scrollThumbnails(index: number) {
  const tablistElem = document.querySelector(`#db-carousel-${uid} [role="tablist"]`) as HTMLElement
  const tabElem = document.querySelector(`#control-tab-${uid}-${index}`) as HTMLElement

  /** If inline scroll exists, try to scroll to the thumbnail */
  if (useIsScrollableInline(tablistElem)) {
    useMaintainScrollVisibilityInline(tabElem, tablistElem)
  }
}

/** Watch slide index to set right animation */
watch(
  currentSlideIndex,
  (newValue, oldValue) => {
    pauseVideo()

    /** Use right transition */
    if (oldValue !== undefined) {
      if (
        (newValue > oldValue && !(oldValue === 0 && newValue === props.length - 1)) ||
        (oldValue === props.length - 1 && newValue === 0)
      ) {
        currentCarouselTransition.value = 'db-slidein-right'
      } else if (newValue < oldValue || (oldValue === 0 && newValue === props.length - 1)) {
        currentCarouselTransition.value = 'db-slidein-left'
      }
    }
  },
  { immediate: true },
)
</script>

<style>
@import '@/assets/styles/components/_carousel.css';
@import '@/assets/styles/animations/_transition-carousel.css';
</style>
